import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody,
  Button,
  FormGroup,
  Label,
  Input,
  Select
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';

const SettingsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FlexRow = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Column = styled.div`
  flex: 1;
`;

const Divider = styled.div`
  width: 1px;
  background: var(--border-color);
  margin: 0 1rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SettingsSection = styled(Card)`
  margin-bottom: 2rem;
`;

const SuccessMessage = styled.div`
  color: var(--success-color);
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: var(--border-radius);
  padding: 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const StoreKeeperSettings: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    theme: 'light',
    language: 'en',
    emailNotifications: true,
    smsNotifications: false,
    systemNotifications: true,
    twoFactorAuth: false,
    autoLogout: 30,
    currency: 'TZS',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h'
  });
  
  const [successMessage, setSuccessMessage] = useState('');

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = () => {
    // In a real application, you would call an API here
    setSuccessMessage('Settings saved successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleChangePassword = () => {
    // Navigate to profile page for password change
    window.location.href = '/storekeeper/profile';
  };

  return (
    <SettingsContainer>
      <h1 className="page-title">Store Keeper Settings</h1>
      
      {successMessage && (
        <SuccessMessage>{successMessage}</SuccessMessage>
      )}
      
      <FlexRow>
        <Column>
          <SettingsSection>
            <CardHeader>
              <h3>Appearance & Language</h3>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label htmlFor="theme">Theme</Label>
                <Select
                  id="theme"
                  value={settings.theme}
                  onChange={(e) => handleSettingChange('theme', e.target.value)}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Auto</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="language">Language</Label>
                <Select
                  id="language"
                  value={settings.language}
                  onChange={(e) => handleSettingChange('language', e.target.value)}
                >
                  <option value="en">English</option>
                  <option value="sw">Swahili</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="currency">Currency</Label>
                <Select
                  id="currency"
                  value={settings.currency}
                  onChange={(e) => handleSettingChange('currency', e.target.value)}
                >
                  <option value="TZS">Tanzanian Shilling (TZS)</option>
                  <option value="USD">US Dollar (USD)</option>
                  <option value="EUR">Euro (EUR)</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="dateFormat">Date Format</Label>
                <Select
                  id="dateFormat"
                  value={settings.dateFormat}
                  onChange={(e) => handleSettingChange('dateFormat', e.target.value)}
                >
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="timeFormat">Time Format</Label>
                <Select
                  id="timeFormat"
                  value={settings.timeFormat}
                  onChange={(e) => handleSettingChange('timeFormat', e.target.value)}
                >
                  <option value="24h">24-hour</option>
                  <option value="12h">12-hour</option>
                </Select>
              </FormGroup>
            </CardBody>
          </SettingsSection>
        </Column>
        
        <Divider />
        
        <Column>
          <SettingsSection>
            <CardHeader>
              <h3>Notifications</h3>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={settings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Email Notifications
                </Label>
              </FormGroup>
              
              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={settings.smsNotifications}
                    onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  SMS Notifications
                </Label>
              </FormGroup>
              
              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={settings.systemNotifications}
                    onChange={(e) => handleSettingChange('systemNotifications', e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  System Notifications
                </Label>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="autoLogout">Auto Logout (minutes)</Label>
                <Select
                  id="autoLogout"
                  value={settings.autoLogout}
                  onChange={(e) => handleSettingChange('autoLogout', parseInt(e.target.value))}
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                  <option value={0}>Never</option>
                </Select>
              </FormGroup>
            </CardBody>
          </SettingsSection>
        </Column>
      </FlexRow>
      
      <SettingsSection>
        <CardHeader>
          <h3>Security</h3>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={settings.twoFactorAuth}
                onChange={(e) => handleSettingChange('twoFactorAuth', e.target.checked)}
                style={{ marginRight: '0.5rem' }}
              />
              Enable Two-Factor Authentication
            </Label>
          </FormGroup>
          
          <Button onClick={handleChangePassword}>
            Change Password
          </Button>
        </CardBody>
      </SettingsSection>
      
      <SettingsSection>
        <CardHeader>
          <h3>Office Management Settings</h3>
        </CardHeader>
        <CardBody>
          <p style={{ color: 'var(--dark-gray)', marginBottom: '1rem' }}>
            Configure specific settings for office management tasks.
          </p>
          
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                defaultChecked={true}
                style={{ marginRight: '0.5rem' }}
              />
              Auto-calculate totals in financial entries
            </Label>
          </FormGroup>
          
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                defaultChecked={true}
                style={{ marginRight: '0.5rem' }}
              />
              Require approval for expenses above 1,000,000 TZS
            </Label>
          </FormGroup>
          
          <FormGroup>
            <Label>
              <input
                type="checkbox"
                defaultChecked={false}
                style={{ marginRight: '0.5rem' }}
              />
              Send daily financial summary reports
            </Label>
          </FormGroup>
        </CardBody>
      </SettingsSection>
      
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <Button onClick={handleSaveSettings}>
          Save All Settings
        </Button>
      </div>
    </SettingsContainer>
  );
};

export default StoreKeeperSettings; 